import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Star, Filter, Grid3X3, LayoutGrid, ChevronLeft, ChevronRight, X, Heart } from 'lucide-react';
import { productsAPI, categoriesAPI } from '../lib/api';
import { getImageUrl } from '../lib/utils';
import SEOHead from '../components/SEOHead';
import { fetchWithCache } from '../lib/cache';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useLoadingToast } from '../hooks/use-loading-toast';

const ProductCard = ({ product, view }) => {
  const { addItem, getMinQty } = useCart();
  const { isWholesale, user } = useAuth();
  const navigate = useNavigate();

  // Determine which price to show based on user role
  let displayPrice = product.selling_price;
  let showWholesaleBadge = false;

  if (user?.role === 'pos_user') {
    // POS users see only POS price
    displayPrice = product.pos_price || product.selling_price;
  } else if (isWholesale && product.wholesale_price) {
    // Wholesale users see only wholesale price
    displayPrice = product.wholesale_price;
    showWholesaleBadge = true;
  }

  const discount = Math.round(((product.mrp - displayPrice) / product.mrp) * 100);

  // Determine minimum quantity based on user role
  const minQty = getMinQty(product);

  const handleBuyNow = (e) => {
    e.stopPropagation();
    addItem(product, minQty);
    if (user) {
      navigate('/checkout');
    } else {
      navigate('/login?redirect=/checkout');
    }
  };

  // Hide Buy Now for POS and Wholesale users
  const showBuyNow = user?.role !== 'pos_user' && !isWholesale;

  if (view === 'list') {
    return (
      <Card
        className="flex gap-4 p-4 hover:shadow-soft-lg transition-shadow cursor-pointer"
        onClick={() => navigate(`/products/${product.id}`)}
        data-testid={`product-card-${product.id}`}
      >
        <img
          src={getImageUrl(product.images?.[0]) || 'https://images.unsplash.com/photo-1624927637280-f033784c1279?w=500'}
          alt={product.name}
          className="w-32 h-32 object-cover rounded-lg"
          loading="lazy"
          decoding="async"
        />
        <div className="flex-1">
          <h3 className="font-semibold hover:text-primary transition-colors">{product.name}</h3>
          <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{product.description}</p>
          <div className="flex items-center gap-1 mt-2">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            <span className="text-sm">4.5 (120 reviews)</span>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-xl font-bold price-tag text-primary">
              ₹{displayPrice.toLocaleString()}
            </span>
            <span className="text-sm text-muted-foreground line-through">₹{product.mrp.toLocaleString()}</span>
            {discount > 0 && <Badge className="discount-badge">{discount}% OFF</Badge>}
            {minQty > 1 && (
              <Badge variant="outline" className="text-xs border-primary text-primary">
                Min Qty: {minQty}
              </Badge>
            )}
          </div>
        </div>
        <div className="self-center flex flex-col gap-2">
          <Button
            className="btn-primary w-full"
            onClick={(e) => { e.stopPropagation(); addItem(product, minQty); }}
          >
            Add to Cart
          </Button>
          {showBuyNow && (
            <Button
              className="bg-accent hover:bg-amber-600 text-black w-full"
              onClick={handleBuyNow}
            >
              Buy Now
            </Button>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card
      className="card-product group cursor-pointer"
      onClick={() => navigate(`/products/${product.id}`)}
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative aspect-square overflow-hidden">
        <img
          src={getImageUrl(product.images?.[0]) || 'https://images.unsplash.com/photo-1624927637280-f033784c1279?w=500'}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
          decoding="async"
        />
        {discount > 0 && (
          <Badge className="absolute top-2 left-2 discount-badge">{discount}% OFF</Badge>
        )}
        {showWholesaleBadge && (
          <Badge className="absolute top-2 right-2 wholesale-badge">Wholesale</Badge>
        )}

      </div>
      <div className="p-2 sm:p-3">
        <h3 className="font-medium text-xs sm:text-sm line-clamp-2 group-hover:text-primary transition-colors">
          {product.name}
        </h3>
        <div className="flex items-center gap-1 mt-1">
          <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-amber-400 text-amber-400" />
          <span className="text-[10px] sm:text-xs text-muted-foreground">4.5 (120)</span>
        </div>
        <div className="mt-1 sm:mt-2 flex flex-wrap items-baseline gap-1 sm:gap-2">
          <span className="text-base sm:text-lg font-bold price-tag text-primary">
            ₹{displayPrice.toLocaleString()}
          </span>
          <span className="text-sm text-muted-foreground line-through">₹{product.mrp.toLocaleString()}</span>
          {minQty > 1 && (
            <span className="text-xs font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
              Min Qty: {minQty}
            </span>
          )}
        </div>
        <div className={`mt-3 ${showBuyNow ? 'grid grid-cols-2' : 'flex'} gap-2 opacity-0 group-hover:opacity-100 transition-opacity`}>
          <Button
            className="btn-primary w-full text-xs px-2"
            onClick={(e) => { e.stopPropagation(); addItem(product, minQty); }}
          >
            Add
          </Button>
          {showBuyNow && (
            <Button
              className="bg-accent hover:bg-amber-600 text-black w-full text-xs px-2"
              onClick={handleBuyNow}
            >
              Buy Now
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [view, setView] = useState('grid');
  const [pwaCols, setPwaCols] = useState(2);
  const [inputPage, setInputPage] = useState('1'); // Initial page is 1
  const { isWholesale } = useAuth();
  const { showLoadingToast, showErrorToast, dismissLoadingToast } = useLoadingToast();
  const isPwa = typeof window !== 'undefined' && (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );

  // Get limit from URL or default to 20
  const limitFromUrl = searchParams.get('limit');
  const limit = limitFromUrl ? parseInt(limitFromUrl) : 20;

  // Bounds from backend
  const [maxPrice, setMaxPrice] = useState(50000);
  const [minBound, setMinBound] = useState(0);

  // Derived values from URL or Bounds
  const page = parseInt(searchParams.get('page') || '1');
  const categoryId = searchParams.get('category');
  const search = searchParams.get('search');
  const sortBy = searchParams.get('sort') || 'created_at-desc';

  const minPriceFromUrl = searchParams.get('min_price');
  const maxPriceFromUrl = searchParams.get('max_price');

  const minPrice = minPriceFromUrl ? Number(minPriceFromUrl) : minBound;
  const maxPriceParam = maxPriceFromUrl ? Number(maxPriceFromUrl) : maxPrice;

  const [priceRange, setPriceRange] = useState([minPrice, maxPriceParam]);

  // Sync priceRange state when bounds or URL params change
  useEffect(() => {
    setPriceRange([minPrice, maxPriceParam]);
  }, [minPrice, maxPriceParam]);

  const fetchProducts = useCallback(async () => {
    const params = {
      page,
      limit,
      sort_by: sortBy?.split('-')[0] || 'created_at',
      sort_order: sortBy?.split('-')[1] || 'desc',
      min_price: minPrice > 0 ? minPrice : undefined,
      max_price: maxPriceParam < maxPrice ? maxPriceParam : undefined,
      include_inactive: false,
    };

    if (categoryId) params.category_id = categoryId;
    if (search) params.search = search;
    if (isWholesale) params.use_wholesale_price = true;

    // Unique cache key based on params
    const cacheKey = `products_${JSON.stringify(params)}`;

    try {
      await fetchWithCache(
        cacheKey,
        async () => {
          const { data } = await productsAPI.getAll(params);
          return data;
        },
        (data, isCached) => {
          if (data) {
            setProducts(data.products || []);
            setTotalPages(data.pages || 1);

            // Only update bounds if they change significantly or on first load
            // This prevents slider jumping while dragging
            if (data.price_range) {
              const { min, max } = data.price_range;
              // Only update if current bounds are default or significantly different
              // Check prevents infinite loop if bounds invoke fetch
              if (minBound !== min) setMinBound(min);
              if (maxPrice !== max) setMaxPrice(max);
            }
          }
        },
        {
          onLoading: (isLoading) => {
            setLoading(isLoading);
            if (isLoading) {
              showLoadingToast('products-load', {
                title: 'Loading Products...',
                description: 'Fetching the latest products for you.'
              });
            } else {
              dismissLoadingToast('products-load');
            }
          },
          onError: (error) => {
            console.error('Failed to fetch products:', error);
            showErrorToast('products-load', {
              title: 'Error',
              description: 'Failed to load products. Please try again.'
            });
          }
        }
      );
    } catch (error) {
      console.error('Fetch products error:', error);
    }
  }, [
    page,
    limit,
    sortBy,
    minPrice,
    maxPriceParam,
    categoryId,
    search,
    isWholesale,
    maxPrice,
    minBound,
    showLoadingToast,
    dismissLoadingToast,
    showErrorToast
  ]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoriesAPI.getFlat();
        setCategories(response.data || []);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };

    fetchCategories();
    fetchProducts();
    setInputPage(String(page)); // Sync input box
  }, [fetchProducts, page, categoryId, search, sortBy, minPriceFromUrl, maxPriceFromUrl, isWholesale, limit]);

  const updateFilters = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }

    // Only reset page if we're not updating the page itself
    if (key !== 'page') {
      params.set('page', '1');
    }

    setSearchParams(params);
  };

  const updatePriceRange = (range) => {
    const params = new URLSearchParams(searchParams);
    params.set('min_price', range[0]);
    params.set('max_price', range[1]);
    params.set('page', '1');
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchParams({});
    setPriceRange([0, maxPrice]);
  };

  const activeFiltersCount = () => {
    let count = 0;
    if (categoryId) count++;
    if (search) count++;
    if (minPrice > 0 || maxPriceParam < maxPrice) count++;
    return count;
  };

  // Helper to build category tree from flat list
  const buildCategoryTree = (cats) => {
    const tree = [];
    const map = {};

    // First pass: create nodes
    cats.forEach(cat => {
      map[cat.id] = { ...cat, children: [] };
    });

    // Second pass: link parents and children
    cats.forEach(cat => {
      if (cat.parent_id && map[cat.parent_id]) {
        map[cat.parent_id].children.push(map[cat.id]);
      } else {
        tree.push(map[cat.id]);
      }
    });

    // Sort function that respects sort_order
    const sortByOrder = (a, b) => {
      if (a.sort_order !== b.sort_order) {
        return a.sort_order - b.sort_order;
      }
      return a.name.localeCompare(b.name);
    };

    // Sort children recursively
    const sortChildren = (node) => {
      if (node.children && node.children.length > 0) {
        node.children.sort(sortByOrder);
        node.children.forEach(sortChildren);
      }
    };

    // Sort root level and all children
    tree.sort(sortByOrder);
    tree.forEach(sortChildren);

    return tree;
  };

  const getRootCategoryId = (id) => {
    if (!id) return null;
    const map = new Map(categories.map((c) => [String(c.id), c]));
    let current = map.get(String(id));
    let guard = 0;
    while (current && current.parent_id && guard < 20) {
      const next = map.get(String(current.parent_id));
      if (!next) break;
      current = next;
      guard += 1;
    }
    return current ? String(current.id) : null;
  };

  const rootCategoryId = getRootCategoryId(categoryId);
  const rootCategories = buildCategoryTree(categories);

  const getPwaGridColsClass = () => {
    if (pwaCols === 1) return 'grid-cols-1';
    if (pwaCols === 2) return 'grid-cols-2';
    if (pwaCols === 3) return 'grid-cols-3-xs';
    return 'grid-cols-2 grid-cols-4-xs';
  };

  const CategoryTreeItem = ({ category, level = 0 }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    // Expand if active category is this or a child
    const isActive = categoryId === category.id;
    const hasActiveChild = (cat) => {
      if (cat.id === categoryId) return true;
      return cat.children?.some(child => hasActiveChild(child));
    };

    useEffect(() => {
      if (hasActiveChild(category)) {
        setIsExpanded(true);
      }
    }, [categoryId]);

    const hasChildren = category.children && category.children.length > 0;

    return (
      <div className="select-none">
        <div
          className={`flex items-center justify-between px-3 py-2 rounded-lg transition-colors cursor-pointer text-sm mb-1 ${isActive
            ? 'bg-primary/10 text-primary font-medium'
            : 'hover:bg-muted text-muted-foreground hover:text-foreground'
            }`}
          style={{ paddingLeft: `${level * 12 + 12}px` }}
          onClick={(e) => {
            e.stopPropagation();
            updateFilters('category', category.id);
          }}
        >
          <span className="truncate">{category.name}</span>
          {hasChildren && (
            <div
              className="p-1 hover:bg-background rounded-md transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
            >
              {isExpanded ? (
                <ChevronRight className="w-3 h-3 transform rotate-90 transition-transform" />
              ) : (
                <ChevronRight className="w-3 h-3 transition-transform" />
              )}
            </div>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div className="animate-in slide-in-from-top-1 duration-200">
            {category.children.map(child => (
              <CategoryTreeItem key={child.id} category={child} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const FilterSidebar = () => {
    const categoryTree = buildCategoryTree(categories);

    return (
      <div className="space-y-6">
        {/* Active Filters */}
        {activeFiltersCount() > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-sm">Active Filters ({activeFiltersCount()})</h4>
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 text-xs">
                <X className="w-3 h-3 mr-1" />
                Clear All
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {categoryId && (
                <Badge variant="secondary" className="flex items-center gap-1 rounded-md px-2 py-1">
                  {categories.find(c => c.id === categoryId)?.name}
                  <X className="w-3 h-3 cursor-pointer hover:text-destructive" onClick={() => updateFilters('category', '')} />
                </Badge>
              )}
              {search && (
                <Badge variant="secondary" className="flex items-center gap-1 rounded-md px-2 py-1">
                  "{search}"
                  <X className="w-3 h-3 cursor-pointer hover:text-destructive" onClick={() => updateFilters('search', '')} />
                </Badge>
              )}
              {(minPrice > 0 || maxPriceParam < maxPrice) && (
                <Badge variant="secondary" className="flex items-center gap-1 rounded-md px-2 py-1">
                  ₹{minPrice} - ₹{maxPriceParam}
                  <X className="w-3 h-3 cursor-pointer hover:text-destructive" onClick={() => updatePriceRange([0, maxPrice])} />
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Categories */}
        <div className="pb-4 border-b">
          <h4 className="font-semibold mb-3 text-sm">Categories</h4>
          <div className="space-y-0.5">
            <div
              className={`cursor-pointer px-3 py-2 rounded-lg transition-colors text-sm mb-1 ${!categoryId
                ? 'bg-primary/10 text-primary font-medium'
                : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                }`}
              onClick={() => updateFilters('category', '')}
            >
              All Products
            </div>
            {categoryTree.map((cat) => (
              <CategoryTreeItem key={cat.id} category={cat} />
            ))}
          </div>
        </div>

        {/* Price Range */}
        <div className="pb-4 border-b">
          <h4 className="font-semibold mb-3 text-sm">Price Range</h4>
          <div className="space-y-4 px-1">
            <div>
              <div className="mb-4">
                <div className="px-2 py-2">
                  <Slider
                    value={priceRange}
                    onValueChange={setPriceRange}
                    onValueCommit={(value) => {
                      if (Array.isArray(value) && value.length === 2) {
                        const validRange = [
                          Math.max(minBound, Math.min(value[0], value[1])),
                          Math.min(maxPrice, Math.max(value[0], value[1]))
                        ];
                        updatePriceRange(validRange);
                      }
                    }}
                    min={minBound}
                    max={maxPrice}
                    step={Math.max(1, Math.ceil((maxPrice - minBound) / 200))}
                    className="w-full"
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-2 px-2">
                  <span>₹{minBound.toLocaleString()}</span>
                  <span>₹{maxPrice.toLocaleString()}</span>
                </div>
                <div className="text-center text-xs font-medium text-primary mt-2 bg-primary/5 rounded-md py-1">
                  Selected: ₹{priceRange[0]?.toLocaleString() || 0} - ₹{priceRange[1]?.toLocaleString() || 0}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="grid gap-1 flex-1">
                  <Label className="text-xs text-muted-foreground">Min Price</Label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">₹</span>
                    <Input
                      type="number"
                      value={priceRange[0] || minBound}
                      onChange={(e) => {
                        const value = Math.max(minBound, parseInt(e.target.value) || minBound);
                        const maxVal = priceRange[1] || maxPrice;
                        setPriceRange([value, Math.max(value, maxVal)]);
                      }}
                      onBlur={() => updatePriceRange(priceRange)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          updatePriceRange(priceRange);
                        }
                      }}
                      min={minBound}
                      max={priceRange[1] || maxPrice}
                      className="h-8 pl-5 text-xs"
                      placeholder={minBound.toString()}
                    />
                  </div>
                </div>
                <div className="text-muted-foreground text-sm">to</div>
                <div className="grid gap-1 flex-1">
                  <Label className="text-xs text-muted-foreground">Max Price</Label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">₹</span>
                    <Input
                      type="number"
                      value={priceRange[1] || maxPrice}
                      onChange={(e) => {
                        const value = Math.min(maxPrice, parseInt(e.target.value) || maxPrice);
                        const minVal = priceRange[0] || minBound;
                        setPriceRange([Math.min(minVal, value), value]);
                      }}
                      onBlur={() => updatePriceRange(priceRange)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          updatePriceRange(priceRange);
                        }
                      }}
                      min={priceRange[0] || minBound}
                      max={maxPrice}
                      className="h-8 pl-5 text-xs"
                      placeholder={maxPrice.toString()}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Price Filters */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-sm">Quick Filters</h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => updatePriceRange([minBound, maxPrice])}
              className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
            >
              Reset
            </Button>
          </div>
          <div className="space-y-1">
            {[
              { label: 'Under ₹500', min: 0, max: 500 },
              { label: '₹500 - ₹1,000', min: 500, max: 1000 },
              { label: '₹1,000 - ₹5,000', min: 1000, max: 5000 },
              { label: '₹5,000 - ₹10,000', min: 5000, max: 10000 },
              { label: 'Above ₹10,000', min: 10000, max: 1000000 },
            ].filter(range => range.min < maxPrice && range.max > minBound).map((range) => {
              // Clamp range to bounds for display/selection
              const effectiveMin = Math.max(range.min, minBound);
              const effectiveMax = Math.min(range.max, maxPrice);
              const isActive = (priceRange[0] || minBound) === effectiveMin && (priceRange[1] || maxPrice) === effectiveMax;

              return (
                <div
                  key={range.label}
                  className={`cursor-pointer px-3 py-2 rounded-lg transition-all duration-200 text-sm border ${isActive
                    ? 'bg-primary/10 text-primary font-medium border-primary/30 shadow-soft'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground border-transparent hover:border-slate-200 dark:hover:border-slate-700'
                    }`}
                  onClick={() => updatePriceRange([effectiveMin, effectiveMax])}
                >
                  <div className="flex items-center justify-between">
                    <span>{range.label}</span>
                    {isActive && (
                      <div className="w-2 h-2 rounded-full bg-primary"></div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const pageTitle = search ? `Search results for "${search}"` :
    categoryId ? (categories.find(c => c.id === categoryId)?.name || 'Products') :
      'All Products';

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-4 py-3 sm:py-8" data-testid="products-page">
      <SEOHead title={pageTitle} />
      <div className="flex gap-8">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:block w-64 flex-shrink-0">
          <FilterSidebar />
        </aside>

        {/* Main Content */}
        <div className="flex-1">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">
                {search ? `Search: "${search}"` : categoryId ? categories.find(c => c.id === categoryId)?.name || 'Products' : 'All Products'}
              </h1>
              <p className="text-muted-foreground">{products.length} products found</p>
            </div>

            <div className="flex items-center gap-3">
              {/* Mobile Filter */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="lg:hidden">
                    <Filter className="w-4 h-4 mr-2" />
                    Filters
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="pwa-filter-sheet">
                  <SheetHeader>
                    <SheetTitle>Filters</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterSidebar />
                  </div>
                </SheetContent>
              </Sheet>

              {/* Sort */}
              <Select value={sortBy} onValueChange={(value) => updateFilters('sort', value)}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at-desc">Newest First</SelectItem>
                  <SelectItem value="created_at-asc">Oldest First</SelectItem>
                  <SelectItem value="selling_price-asc">Price: Low to High</SelectItem>
                  <SelectItem value="selling_price-desc">Price: High to Low</SelectItem>
                  <SelectItem value="name-asc">Name: A-Z</SelectItem>
                  <SelectItem value="name-desc">Name: Z-A</SelectItem>
                  <SelectItem value="mrp-desc">MRP: High to Low</SelectItem>
                  <SelectItem value="stock_qty-desc">Stock: High to Low</SelectItem>
                </SelectContent>
              </Select>

              {/* View Toggle */}
              <div className="hidden sm:flex border rounded-lg">
                <Button
                  variant={view === 'grid' ? 'secondary' : 'ghost'}
                  size="icon"
                  onClick={() => setView('grid')}
                >
                  <Grid3X3 className="w-4 h-4" />
                </Button>
                <Button
                  variant={view === 'list' ? 'secondary' : 'ghost'}
                  size="icon"
                  onClick={() => setView('list')}
                >
                  <LayoutGrid className="w-4 h-4" />
                </Button>
              </div>
              {isPwa && view === 'grid' && (
                <div className="pwa-only border rounded-lg flex">
                  {[1, 2, 3, 4].map((count) => (
                    <Button
                      key={count}
                      variant={pwaCols === count ? 'secondary' : 'ghost'}
                      size="icon"
                      onClick={() => setPwaCols(count)}
                    >
                      {count}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* PWA Parent Categories Row */}
          {rootCategories.length > 0 && (
            <div className="pwa-only pwa-category-row" aria-label="Parent categories">
              <button
                type="button"
                className={`pwa-category-pill ${!categoryId ? 'is-active' : ''}`}
                onClick={() => updateFilters('category', '')}
              >
                All
              </button>
              {rootCategories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`pwa-category-pill ${rootCategoryId === String(cat.id) ? 'is-active' : ''}`}
                  onClick={() => updateFilters('category', cat.id)}
                >
                  {cat.image_url && (
                    <img
                      src={getImageUrl(cat.image_url)}
                      alt=""
                      className="pwa-category-icon"
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                  <span>{cat.name}</span>
                </button>
              ))}
            </div>
          )}

          {/* Products Grid */}
          {loading ? (
            <div className={`grid ${view === 'grid'
              ? (isPwa ? getPwaGridColsClass() : 'grid-cols-2 grid-cols-3-xs sm:grid-cols-4 lg:grid-cols-5')
              : 'grid-cols-1'} gap-2 sm:gap-5`}>
              {[...Array(8)].map((_, i) => (
                <div key={i} className={`card-product ${view === 'list' ? 'flex gap-4 p-4' : ''}`}>
                  {view === 'list' ? (
                    <>
                      <div className="w-32 h-32 bg-muted rounded-lg animate-pulse flex-shrink-0" />
                      <div className="flex-1 space-y-3">
                        <div className="h-5 w-3/4 bg-muted rounded animate-pulse" />
                        <div className="h-4 w-full bg-muted rounded animate-pulse" />
                        <div className="h-4 w-1/2 bg-muted rounded animate-pulse" />
                        <div className="h-6 w-24 bg-muted rounded animate-pulse" />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="aspect-square bg-muted rounded-xl animate-pulse" />
                      <div className="p-3 space-y-2">
                        <div className="h-4 w-full bg-muted rounded animate-pulse" />
                        <div className="h-3 w-20 bg-muted rounded animate-pulse" />
                        <div className="h-5 w-24 bg-muted rounded animate-pulse" />
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No products found</p>
            </div>
          ) : (
            <div className={`grid ${view === 'grid'
              ? (isPwa ? getPwaGridColsClass() : 'grid-cols-2 grid-cols-3-xs sm:grid-cols-4 lg:grid-cols-5')
              : 'grid-cols-1'} gap-2 sm:gap-5`}>
              {products.map((product) => (
                <ProductCard key={product.id} product={product} view={view} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-8 pb-8">
              {/* Items per page selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Show:</span>
                <Select
                  value={String(limit)}
                  onValueChange={(value) => updateFilters('limit', value)}
                >
                  <SelectTrigger className="w-20 h-8">
                    <SelectValue placeholder="20" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="80">80</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">per page</span>
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => updateFilters('page', String(page - 1))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>

                <span className="text-sm text-muted-foreground whitespace-nowrap px-2">
                  Page {page} of {totalPages}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => updateFilters('page', String(page + 1))}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>

                {/* Jump to Page */}
                <div className="flex items-center gap-2 ml-4">
                  <span className="text-sm text-muted-foreground">Go to:</span>
                  <Input
                    className="h-8 w-16 text-center"
                    value={inputPage}
                    onChange={(e) => setInputPage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const p = parseInt(inputPage);
                        if (!isNaN(p) && p >= 1 && p <= totalPages) {
                          updateFilters('page', String(p));
                        }
                      }
                    }}
                    onBlur={() => {
                      const p = parseInt(inputPage);
                      if (!isNaN(p) && p >= 1 && p <= totalPages) {
                        updateFilters('page', String(p));
                      } else {
                        setInputPage(String(page)); // Reset if invalid
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
